import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import TextInput from '../../forms/TextInput';
import SelectMenu from '../../forms/SelectMenu';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { ServicePlanServiceArea } from '../../../data/models/client-program.model';
import { ZipCodeRange } from '../../../data/models/service-plan.model';

const PREFIX = 'ServiceAreaDialog';

const classes = {
  content: `${PREFIX}-content`,
  label: `${PREFIX}-label`,
  zipCode: `${PREFIX}-zipCode`,
  stateCode: `${PREFIX}-stateCode`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.label}`]: {
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.zipCode}`]: {
    width: 121,
  },
  [`& .${classes.stateCode}`]: {
    width: 121,
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

interface ServiceAreaDialogProps {
  title: string;
  clientBC: string;
  serviceArea?: ServicePlanServiceArea;
  open: boolean;
  close: () => void;
}

export default function ServiceAreaDialog({
  title,
  clientBC,
  serviceArea,
  open,
  close,
}: ServiceAreaDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const stateCodes = useAppSelector(lookupSlice.selectStateCodes);
  const savingServiceArea = useAppSelector(clientSlice.selectSavingServiceArea);
  const savedServiceArea = useAppSelector(clientSlice.selectSavedServiceArea);

  const formHook = useForm<{
    name: string;
    zipCodes: (ZipCodeRange & { isRange: boolean })[];
  }>({
    defaultValues: {
      name: '',
      zipCodes: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: formHook.control,
    name: 'zipCodes',
  });
  const zipCodes = formHook.watch('zipCodes');

  useEffect(() => {
    formHook.reset({
      name: serviceArea?.name || '',
      zipCodes: (serviceArea?.zipCodes || []).map((zipCode) => ({
        ...zipCode,
        isRange: !zipCode.zipCode,
      })),
    });
  }, [serviceArea]);

  useEffect(() => {
    if (savedServiceArea) {
      handleClose();
      dispatch(clientSlice.setSavedServiceArea(false));
    }
  }, [savedServiceArea]);

  const handleSave = (data: { name: string; zipCodes: ZipCodeRange[] }) => {
    dispatch(clientSlice.setSavedServiceArea(false));
    const newServiceArea: ServicePlanServiceArea = {
      serviceAreaUUID: serviceArea?.serviceAreaUUID || undefined,
      clientBC,
      name: data.name,
      zipCodes: data.zipCodes.map((zipCode) => ({
        zipCode: zipCode.zipCode || undefined,
        zipCodeBegin: zipCode.zipCodeBegin || undefined,
        zipCodeEnd: zipCode.zipCodeEnd || undefined,
        stateCode: zipCode.stateCode,
      })),
    };
    if (serviceArea) {
      dispatch(clientSlice.updateClientServiceAreaAsync(newServiceArea));
    } else {
      dispatch(clientSlice.addClientServiceAreaAsync(newServiceArea));
    }
  };

  const handleClose = () => {
    formHook.reset();
    close();
  };

  const handleAddZipCode = () => {
    append({
      zipCode: '',
      zipCodeBegin: '',
      zipCodeEnd: '',
      stateCode: '',
      isRange: false,
    });
  };

  return (
    <RootDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <TextInput
            name="name"
            label={t('client.serviceAreaName')}
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
            errors={formHook.formState.errors}
          />

          {fields.map((zipCode, index) => (
            <Box key={zipCode.id} display="flex" gap={1}>
              <FormControlLabel
                label={t('client.zipCodeRange')}
                className={classes.label}
                control={
                  <Checkbox
                    checked={zipCodes[index]?.isRange}
                    onChange={(e) =>
                      formHook.setValue(
                        'zipCodes',
                        zipCodes.map((zip, i) =>
                          i === index
                            ? {
                                ...zip,
                                isRange: e.target.checked,
                                zipCode: '',
                                zipCodeBegin: '',
                                zipCodeEnd: '',
                              }
                            : zip,
                        ),
                      )
                    }
                  />
                }
              />
              {!zipCode.isRange ? (
                <TextInput
                  name={`zipCodes[${index}].zipCode`}
                  label={t('client.zipCode')}
                  className={classes.zipCode}
                  control={formHook.control}
                  required={true}
                  inputProps={{ maxLength: 5 }}
                  rules={{
                    required: requiredFieldMessage,
                    pattern: {
                      value: /([0-9]{5})/,
                      message: 'Enter a 5 digit zip code',
                    },
                  }}
                  errors={formHook.formState.errors}
                />
              ) : (
                <>
                  <TextInput
                    name={`zipCodes[${index}].zipCodeBegin`}
                    label={t('client.startZipCode')}
                    className={classes.zipCode}
                    control={formHook.control}
                    required={true}
                    inputProps={{ maxLength: 5 }}
                    rules={{
                      required: requiredFieldMessage,
                      pattern: {
                        value: /([0-9]{5})/,
                        message: 'Enter a 5 digit zip code',
                      },
                    }}
                    errors={formHook.formState.errors}
                  />
                  <TextInput
                    name={`zipCodes[${index}].zipCodeEnd`}
                    label={t('client.endZipCode')}
                    className={classes.zipCode}
                    control={formHook.control}
                    required={true}
                    inputProps={{ maxLength: 5 }}
                    rules={{
                      required: requiredFieldMessage,
                      pattern: {
                        value: /(\d{5})/,
                        message: 'Enter a 5 digit zip code',
                      },
                    }}
                    errors={formHook.formState.errors}
                  />
                </>
              )}
              <SelectMenu
                name={`zipCodes[${index}].stateCode`}
                label={t('client.state')}
                className={classes.stateCode}
                values={stateCodes}
                valueAttr="stateCode"
                labelAttr="stateName"
                control={formHook.control}
                required={true}
                rules={{ required: requiredFieldMessage }}
              />
              <IconButton
                data-testid="clear-test"
                onClick={() => remove(index)}>
                <ClearIcon />
              </IconButton>
            </Box>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleAddZipCode}>
          {t('client.addZipCode')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          disabled={savingServiceArea}
          onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!formHook.formState.isValid || savingServiceArea}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
